import React, { ChangeEvent, useEffect, useMemo } from "react";
import { Spring, animated } from "@react-spring/web";
import useMeasure from "react-use-measure";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Form } from "react-bootstrap";

import Actions from "src/components/actionbar";
import Thumbnail from "src/components/thumbnail";
import { POSITIONS } from "src/constants/banner";
import Tooltip from "src/components/tooltip";
import LiveConfirmationDialog from "../liveConfirmationDialog";
import { useUploadMedia } from "src/query/settings/uploadMedia";

import { MenuState } from "src/types/settings/accountManagement";
import { MediaFile } from "../../../../../types/media";
import { parseMediaMetadata } from "src/utils/media.service";

import { useBannerAdoption } from "src/query/settings/adoption/accountManagement";
import { BannerFiltered, CropBannerClicked, FilterBannerClicked } from "src/types/adoption/settings/accountManagement";

import _ from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";

interface MenubarProps {
  entitySettings: any;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: MenuState) => void;
  onChanges: (field: string, value: any) => void;
  enableLiveToggle?: boolean;
  entityField: string;
}

const Menubar: React.FC<MenubarProps> = ({ entitySettings, menuState, onChange, onChanges, enableLiveToggle,entityField}) => {

  const actionBarRef = React.useRef<HTMLDivElement>(null);

  const [viewMenuRef, viewMenuBounds] = useMeasure();
  const [editMenuRef, editMenuBounds] = useMeasure();
  const [bannerMenuRef, bannerMenuBounds] = useMeasure();
  const [confirmationMenuRef, confirmationMenuBounds] = useMeasure();
  const [positionMenuRef, positionMenuBounds] = useMeasure();

  const showCrop = menuState.primaryAction === 'edit-banner' && (_.includes(['crop', 'apply-crop'], menuState.secondaryAction));
  const showFilter = menuState.primaryAction === 'edit-banner' && (_.includes(['filter'], menuState.secondaryAction));
  const showConfirmation = showCrop || showFilter;
  const showPosition = menuState.primaryAction === 'edit-banner' && (_.includes(['position'], menuState.secondaryAction));

  const [dimensions, setDimensions] = React.useState<{ width: number | string, height: number | string }>({
    width: 'auto',
    height: 0
  });

  const {trackBannerCropped, trackBannerFilteredEvent} = useBannerAdoption();

  const handleMenuChange = (value) => {
    onChange({
      ...menuState,
      ...value
    });
  }

  const handleConfirmationClick = () => {
    if (showCrop) {
      handleMenuChange({
        ...menuState,
        secondaryAction: 'apply-crop'
      });

      trackBannerCropped({
        bannerType: entitySettings?.banner?.background?.type || "",
        sourceCta: "Settings"
      });

    } else if (showFilter) {
      handleMenuChange({
        ...menuState,
        secondaryAction: null
      });

      trackBannerFilteredEvent({
        collectionName: "",
        bannerType: entitySettings?.banner?.background?.type || "",
        sourceCta: "Settings"
      });
      
    }
  }

  const handleConfirmationCancel = () => {
    if (showCrop || showFilter) {
      handleMenuChange({
        secondaryAction: null,
        banner: {
          ...entitySettings?.banner,
        },
      });
    }
  };


  useEffect(() => {
    let width = 0;
    let height = 0;
    if (menuState.primaryAction === 'view') {
      width = viewMenuBounds.width || 0;
      height = viewMenuBounds.height || 0;
    } else if (menuState.primaryAction === 'edit') {
      width = editMenuBounds.width || 0;
      height = editMenuBounds.height || 0;
    } else if (menuState.primaryAction === 'edit-banner' && !menuState.secondaryAction) {
      width = bannerMenuBounds.width || 0;
      height = bannerMenuBounds.height || 0;
    } else if (showConfirmation) {
      width = confirmationMenuBounds.width || 0;
      height = confirmationMenuBounds.height || 0;
    } else if (showPosition) {
      width = positionMenuBounds.width || 0;
      height = positionMenuBounds.height || 0;
    }

    setDimensions({
      width: width + 20,
      height: height + 20
    });

  }, [menuState.primaryAction, viewMenuBounds, editMenuBounds, bannerMenuBounds, confirmationMenuBounds, positionMenuBounds, showConfirmation, showPosition]);

  return (
    <Spring to={{ width: dimensions.width, height: dimensions.height }} config={{
      friction: 14,
      tension: 120,
    }}>
      {props => (
        <animated.div ref={actionBarRef} style={props} className={styles.actionBar}>
          <div className={cx(styles.menu, styles.viewMenu, { [styles.show]: menuState.primaryAction === 'view' })}>
            <div ref={viewMenuRef} className={styles.inner}>
              <ViewMenu entity={entitySettings} menuState={menuState} onMenuChange={handleMenuChange} onChanges={onChanges} enableLiveToggle={enableLiveToggle} entityField ={entityField}/>
            </div>
          </div>

          <div className={cx(styles.menu, styles.bannerMenu, { [styles.show]: menuState.primaryAction === 'edit-banner' && !menuState.secondaryAction })}>
            <div ref={bannerMenuRef} className={styles.inner}>
              <EditBannerMenu
                entity={entitySettings}
                menuState={menuState}
                onMenuChange={handleMenuChange}
                onChanges={onChanges}
                entityField={entityField}
              />
            </div>
          </div>

          <div className={cx(styles.menu, styles.confirmationMenu, {
            [styles.show]: showConfirmation
          })}>
            <div ref={confirmationMenuRef} className={styles.inner}>
              <ConfirmationMenu onConfirm={handleConfirmationClick} onCancel={handleConfirmationCancel} />
            </div>
          </div>

          <div className={cx(styles.menu, styles.positionMenu, {
            [styles.show]: showPosition
          })}>
            <div ref={positionMenuRef} className={styles.inner}>
              <BannerPositionMenu
                entitySettings={entitySettings}
                menuState={menuState}
                entityField={entityField}
                onMenuChange={handleMenuChange}
                onChange={(field, value) => {
                  onChanges(field, {
                    ...entitySettings,
                    banner: {
                      ...entitySettings?.banner,
                      ...value?.banner,
                    },
                    ...value,
                  });
                }}
              />
            </div>
          </div>
        </animated.div>
      )}
    </Spring>
  );
}

interface ViewMenuProps {
  entity: any;
  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
  onChanges: (field: string, value: any) => void;
  enableLiveToggle?: boolean;
  entityField: string;
}

const ViewMenu: React.FC<ViewMenuProps> = ({ entity, menuState, onMenuChange, onChanges, enableLiveToggle, entityField }) => {

  const [showLive, setShowLive] = React.useState(false);

  const {trackAddBannerClicked} = useBannerAdoption();

  const live = _.get(entity, 'live', false);

  const handleEdit = () => {
    trackAddBannerClicked({ sourceCta: "Settings", collectionName: "" });

    onMenuChange({
      ...menuState,
      primaryAction: 'edit-banner'
    });
  }

  const handleShowLive = () => {
    setShowLive(true);
  }

  const handleShowLiveClose = () => {
    setShowLive(false);
  }

  return (
    <>
      <Actions.Container>
        <Actions.Row>
          <>
            <Actions.Button icon="paperflite-edit" title="Customise banner" onClick={handleEdit} width={4} />
          </>
          {enableLiveToggle && (
          <Actions.IconButton icon="fa-regular fa-circle-dot"
            tooltip={live ? 'Collection is live' : 'Go Live'}
            className={cx({ [styles.activeAction]: live })}
            onClick={handleShowLive}
          />
          )}
          <SharedByEditMenu entity={entity} onChanges={onChanges} entityField={entityField} />
        </Actions.Row>
      </Actions.Container>

      <LiveConfirmationDialog
        show={showLive}
        collection={entity}
        onClose={handleShowLiveClose}
        onSave={(updatedCollectionSettings) => {
          onChanges?.('collectionSettings', updatedCollectionSettings);
        }}
      />
    </>
  );
}

interface EditBannerMenuProps {
  entity: any;
  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
  onChanges: (field: string, value: any) => void;
  entityField: string;
}

const EditBannerMenu: React.FC<EditBannerMenuProps> = ({ entity, menuState, onMenuChange, onChanges,entityField }) => {

  const {
    trackCropBannerClicked,
    trackBannerAdded,
    trackFilterBannerClicked,
    trackBannerFilteredEvent,
  } = useBannerAdoption();

  const originalEntity = React.useRef(entity);
  const [entityState, setEntityState] = React.useState({ ...entity })

  const { mutate: uploadMediaMutate, isPending: uploading } = useUploadMedia();

  const canCrop = useMemo(() => {
    return entityState?.banner?.background?.type === 'image';
  }, [entityState]);


  const handleBannerFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 10MB");
      return;
    }

    const backgroundMedia = await parseMediaMetadata(file);

    const updatedEntity = {
      ...entity,
      banner: {
        ...(entityState.banner || {}),
        background: backgroundMedia,
        crop: null,
      },
    };

    onChanges(entityField, updatedEntity);
  };

  const handleSaveBanner = async () => {
    const banner = _.get(entity, 'banner');
    const media = _.get(banner, 'background');
    const entityType = _.get(media, 'entityType');

    const bannerType = _.get(media, 'type', '');

    if (media && entityType === 'media-preview') {
      const mediaFile = media as MediaFile;
      const blob = await fetch(media!.full!).then(r => r.blob());
      const file = new File([blob], 'banner.png', { type: mediaFile.contentType || blob.type });

      uploadMediaMutate({
        ...media,
        file: file
      } as MediaFile, {
        onSuccess: (response) => {
          const updatedEntity = {
            ...entity,
            banner: {
              ...entity.banner,
              background: response,
            },
          };

          setEntityState(updatedEntity);

          onChanges(entityField, updatedEntity);

          trackBannerAdded({ collectionName: "", bannerType, sourceCta: "Settings" });

          onMenuChange({ ...menuState, primaryAction: 'view' });
        },
        onError: (err) => {
          console.error("Media upload failed", err);
        }
      });

    } else {
      onMenuChange({
        ...menuState,
        primaryAction: 'view'
      });
    }
  };


  const handleCancel = () => {
    setEntityState(originalEntity.current);

    onChanges(entityField, originalEntity.current);

    onMenuChange({ ...menuState, primaryAction: 'view' });
  };

  const handleCrop = () => {

    const cropData: CropBannerClicked = {
      collectionName: "",
      bannerType: entityState?.banner?.background?.type || "",
      sourceCta: "Settings"
    };
  
    trackCropBannerClicked(cropData);

    onMenuChange({
      ...menuState,
      secondaryAction: 'crop'
    });
  }

  const handleFilter = () => {

    const filterData: FilterBannerClicked = {
      collectionName: "",
      bannerType: entityState?.banner?.background?.type || "",
      sourceCta: "Settings"
    };
  
    const filteredEventData: BannerFiltered = {
      collectionName: "",
      bannerType: entityState?.banner?.background?.type || "",
      sourceCta: "Settings"
    };

    trackFilterBannerClicked(filterData);
    trackBannerFilteredEvent(filteredEventData);

    onMenuChange({
      ...menuState,
      secondaryAction: 'filter'
    });
  }

  const handlePosition = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'position'
    });
  }

  React.useEffect(() => {
    setEntityState({ ...entity });
  }, [entity]);

  return (
    <Actions.Container>
      <Actions.Row>
        <Actions.FileUpload width={5}
          icon="paperflite-upload"
          title="Upload from device"
          accept="image/*,video/*"
          disabled={uploading}
          onClick={() => (entity)}
          onChange={handleBannerFileChange}
        />
        {canCrop &&
          <>
            <Actions.IconButton
              icon="paperflite-crop"
              tooltip="Crop"
              disabled={uploading}
              onClick={handleCrop}
            />
            <Actions.IconButton
              icon="paperflite-focus"
              tooltip="Position"
              disabled={uploading}
              onClick={handlePosition}
            />
          </>
        }

        {!canCrop &&

          <Actions.Button
            icon="paperflite-focus"
            width={2}
            title="Position"
            disabled={uploading}
            onClick={handlePosition}
          />
        }

      </Actions.Row>
      <Actions.Row>
        <Actions.Button width={2}
          icon="paperflite-filter-property"
          title="Filters"
          disabled={uploading}
          onClick={handleFilter}
        />
        <Actions.Button width={3}
          icon="paperflite-tick"
          title={uploading ? 'Saving...' : 'Save Changes'}
          className={styles.primaryAction}
          disabled={uploading}
          onClick={handleSaveBanner}
        />
        <Actions.Button width={2}
          icon="paperflite-close"
          title="Cancel"
          disabled={uploading}
          onClick={handleCancel}
        />
      </Actions.Row>
    </Actions.Container>
  );
}

interface ConfirmationMenuProps {
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationMenu: React.FC<ConfirmationMenuProps> = ({ onConfirm, onCancel }) => {
  return (
    <Actions.Container>
      <Actions.Row>
        <Actions.Button width={3} icon="paperflite-tick" className={styles.primaryAction} title="Done" onClick={onConfirm} />
        <Actions.Button width={3} icon="paperflite-close" title="Cancel" onClick={onCancel} />
      </Actions.Row>
    </Actions.Container>
  );
}

interface BannerPositionMenuProps {
  entitySettings: any;
  entityField: string;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
  onChange: (field: string, value: any) => void;
}

const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({ entitySettings, menuState, onMenuChange, onChange, entityField}) => {
  const Banner = entitySettings?.banner || {};
  const positionValue = Banner.position || '';
  
  const handlePositionChange = (position: string) => {
    const currentBanner = entitySettings?.banner || {};
    const updatedBanner = {
      ...currentBanner,
      position,
    };
  
    onChange(entityField, {
      ...entitySettings,
      banner: {
        ...currentBanner,
        ...updatedBanner,
      },
    });
  };

  const handleConfirm = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: null,
    });
  };

  const handleCancel = () => {
    onChange(entityField, {
      ...entitySettings,
      banner: Banner,
    });

    onMenuChange({
      ...menuState,
      secondaryAction: null,
    });
  };

  return (
    <Actions.Container>
      <Actions.Row className={styles.positionActionRow}>
        <Actions.Item className={styles.positions}>
          {POSITIONS.map((position) => {
            const active = position.value === positionValue;
            return (
              <div key={position.value}
                className={cx(styles.position, {
                  [styles.active]: active
                })}
                onClick={() => handlePositionChange(position.value)}
              />
            );
          })}
        </Actions.Item>
        <Actions.Button width={3} icon="paperflite-tick" className={styles.primaryAction} title="Save" onClick={handleConfirm} />
        <Actions.Button width={3} icon="paperflite-close" title="Cancel" onClick={handleCancel} />
      </Actions.Row>
    </Actions.Container>
  );
}
interface SharedByEditMenuProps {
  entity: any;
  onChanges?: (field: string, value: any) => void;
  entityField: string;
}

const SharedByEditMenu: React.FC<SharedByEditMenuProps> = ({ entity, onChanges, entityField }) => {
  const user = useSelector((state: any) => state.users?.me);
  const enabled = _.get(entity, 'banner.sharedByEnabled', false);

  const handleChange = () => {
    const updatedEnabled = !enabled;

    onChanges?.(entityField, {
      ...entity,
      banner: {
        ...entity?.banner,
        sharedByEnabled: updatedEnabled,
      },
    });
  }

  return (
    <Actions.Item className={cx(styles.sharedByMenu, { [styles.disabled]: !enabled })}>
      <div className={styles.userIcon}>
        <Thumbnail media={user?.icon} width={100} height={100} />
      </div>
      <Tooltip placement="auto" content={enabled ? "Hide Shared By" : "Show Shared By"}>
        <Form.Check type="switch" className={styles.switch} checked={enabled} onChange={handleChange} />
      </Tooltip>
    </Actions.Item>
  );
}

export { Menubar, type MenuState };